/* eslint-disable no-nested-ternary */
import { createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import EmptyStateMessage from '~/domains/common/components/EmptyStateMessage/EmptyStateMessate';
import { ErrorScreen } from '~/domains/common/components/ErrorScreen/ErrorScreen';
import LocationDisallowedSingleStatBanner from '~/domains/common/components/LocationInfo/LocationDisallowedSingleStatBanner';
import UnknownErrorScreen from '~/domains/common/components/UnknownErrorScreen/UnknownErrorScreen';
import IconSearchLarge from '~/domains/design-system/icons/IconSearchLarge';
import { isApiError } from '~/domains/services/utils/isApiError';
import { isSplashApiError } from '~/domains/services/utils/isBEApiError';

import { useBoardContext } from '../../context/BoardContext/BoardContext';
import ConnectedPlayerCard from '../PlayerCard/ConnectedPlayerCard';
import Boosts from '../Boosts/Boosts';

import { BoardFilters } from './BoardFilters/BoardFilters';
import BoardPropsList from './BoardPropsList/BoardPropsList';
import ConnectedPicksDrawer from './PicksDrawer/ConnectedPicksDrawer';
import ConnectedPicksPanel from './PicksPanel/ConnectedPicksPanel';
import useBoard from './useBoard';

const useStyles = createStyles((theme) => ({
  wrapper: {
    padding: `${theme.other.spacing._16}px 0`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.other.spacing._16,
    flexGrow: 1,
  },
  picksPanel: {
    position: 'sticky',
    bottom: theme.other.spacing._16,
    [theme.fn.largerThan(theme.other.layout.maxWidth + theme.other.layout.paddingLg * 2)]: {
      marginLeft: -theme.other.spacing._16,
      marginRight: -theme.other.spacing._16,
    },
  },
  boosts: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.other.spacing._16,
  },
}));

function Board() {
  const { t } = useTranslation('contest');
  const { filters, boosts } = useBoardContext();
  const { classes, theme } = useStyles();

  const { propsQuery, items, locationState, isSingleStatPropsAllowed } = useBoard();

  if (propsQuery.error) {
    if (isSplashApiError(propsQuery.error)) {
      return <ErrorScreen title="Error" description={propsQuery.error.response.data.message} />;
    }
    if (isApiError(propsQuery.error)) {
      return (
        <ErrorScreen
          title="Error"
          description={propsQuery.error.response?.statusText ?? propsQuery.error.message}
        />
      );
    }

    return <UnknownErrorScreen />;
  }

  // UI should not work without filters
  const filtersError = filters.query.error;
  if (filtersError) {
    if (isSplashApiError(filtersError)) {
      return <ErrorScreen title="Error" description={filtersError.response.data.message} />;
    }
    if (isApiError(filtersError)) {
      return (
        <ErrorScreen
          title="Error"
          description={filtersError.response?.statusText ?? filtersError.message}
        />
      );
    }

    return <UnknownErrorScreen />;
  }

  return (
    <div className={classes.wrapper}>
      {!isSingleStatPropsAllowed ? (
        <LocationDisallowedSingleStatBanner state={locationState} />
      ) : null}
      <Boosts boosts={boosts} />
      <BoardFilters />
      <BoardPropsList
        items={items}
        hasNextPage={propsQuery.hasNextPage}
        isFetchingNextPage={propsQuery.isFetchingNextPage}
        fetchNextPage={propsQuery.fetchNextPage}
        isFetching={propsQuery.isFetching}
        isLoading={propsQuery.isLoading}
        noResults={
          <EmptyStateMessage
            title={t('quickPick.board.emptyScreen.noProps.title')}
            subtitle={t('quickPick.board.emptyScreen.noProps.subtitle')}
            icon={<IconSearchLarge color={theme.colors.gray2[3]} size={theme.other.spacing._48} />}
          />
        }
      />
      <div className={classes.picksPanel}>
        <ConnectedPicksPanel />
      </div>
      <ConnectedPicksDrawer />
      <ConnectedPlayerCard
        playerId={filters.value.playerCardId}
        opened={!!filters.value.playerCardId}
        onClose={() => filters.set('playerCardId', null)}
      />
    </div>
  );
}

export default Board;
